/**
 * "About this map" — the one paper surface in the app (spec §0 decision 1).
 * Sources and licences, what off-peak drive times mean, the approximate site
 * outline, indicative future infrastructure (status + source dates), and where
 * the place pins came from. Copy: spec §F about.* plus HUD_COPY.
 *
 * A native modal <dialog>: the rest of the app is inert while it is open, and
 * Escape, the scrim and Close put it away. The sheet rises from the ground like
 * paper laid on the map (640 settle) and leaves at 0.7× (448 leave); under
 * reduced motion it cross-fades in 160. Focus starts on the title (the content
 * is read from the top) and returns to whatever opened it.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type JSX } from "react";
import { DUR, Icon, IconButton, Status, exitMs, useReducedMotion } from "@/design";
import { useApp } from "@/store/store";
import { on } from "@/store/bus";
import { HUD_COPY, pinsBreakdown } from "./copy";
import { formatIsoDate, futureRows, sourceCounts } from "./geometry";
import { onAboutRequest } from "./aboutSignal";

type SheetPhase = "closed" | "opening" | "open" | "closing";

/** The brand's one ornament: a ridge line under the heading. */
const RIDGE_D = "M0 17C42 17 70 12 112 12S178 19 226 16 300 5 352 6 430 16 478 14 552 9 600 11";
const REDUCED_MS = 160;

export function AboutSheet({ kiosk }: { kiosk: boolean }): JSX.Element | null {
  const [phase, setPhaseState] = useState<SheetPhase>("closed");
  const phaseRef = useRef<SheetPhase>("closed");
  const setPhase = useCallback((p: SheetPhase) => {
    phaseRef.current = p;
    setPhaseState(p);
  }, []);
  const openerRef = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const timer = useRef(0);
  const reduced = useReducedMotion();
  const titleId = useId();

  const data = useApp((s) => s.data);
  const osmDate = useApp((s) => s.routes?.osmDataDate ?? null);
  const future = useApp((s) => s.geo?.future ?? null);

  const finish = useCallback(() => {
    window.clearTimeout(timer.current);
    const d = dialogRef.current;
    if (d?.open) d.close();
    setPhase("closed");
    const back = openerRef.current;
    openerRef.current = null;
    if (back && back.isConnected) back.focus({ preventScroll: true });
  }, [setPhase]);

  const requestClose = useCallback(() => {
    const p = phaseRef.current;
    if (p === "closed" || p === "closing") return;
    setPhase("closing");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(finish, reduced ? REDUCED_MS : exitMs(DUR.unhurried));
  }, [finish, reduced, setPhase]);

  // Open/close requests from the attribution pill, the layers card and the panel footer.
  useEffect(
    () =>
      onAboutRequest(
        (el) => {
          if (phaseRef.current === "open" || phaseRef.current === "opening") return;
          window.clearTimeout(timer.current);
          if (phaseRef.current === "closed") openerRef.current = el;
          setPhase("opening");
        },
        () => requestClose(),
      ),
    [requestClose, setPhase],
  );

  // Kiosk: every visitor meets a clean map.
  useEffect(() => {
    const a = on("idle:attract", () => requestClose());
    const b = on("idle:reset", () => requestClose());
    return () => {
      a();
      b();
    };
  }, [requestClose]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const mounted = phase !== "closed";

  // showModal once the element exists; close on unmount (StrictMode-safe: the
  // second mount shows it again before the queued close event runs).
  useLayoutEffect(() => {
    const d = dialogRef.current;
    if (!mounted || !d) return undefined;
    if (!d.open) {
      try {
        d.showModal();
      } catch {
        d.setAttribute("open", "");
      }
    }
    titleRef.current?.focus({ preventScroll: true });
    return () => {
      if (d.open) d.close();
    };
  }, [mounted]);

  // Rise on the next frame, so the resting (closed) transform is painted first.
  useEffect(() => {
    if (phase !== "opening") return undefined;
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        if (phaseRef.current === "opening") setPhase("open");
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, [phase, setPhase]);

  // Escape (cancel) animates out; a native close (a second Escape) ends at once.
  useEffect(() => {
    const d = dialogRef.current;
    if (!mounted || !d) return undefined;
    const onCancel = (e: Event) => {
      e.preventDefault();
      requestClose();
    };
    const onClose = () => {
      if (!d.open && phaseRef.current !== "closed") finish();
    };
    d.addEventListener("cancel", onCancel);
    d.addEventListener("close", onClose);
    return () => {
      d.removeEventListener("cancel", onCancel);
      d.removeEventListener("close", onClose);
    };
  }, [mounted, requestClose, finish]);

  const rows = useMemo(() => futureRows(future, HUD_COPY.futureSources, data?.places), [future, data]);
  const counts = useMemo(() => sourceCounts(data?.places), [data]);
  const date = formatIsoDate(osmDate);
  const prepared = formatIsoDate(data?.generatedAt);

  if (!mounted) return null;

  return (
    <dialog
      ref={dialogRef}
      className="ld-loc-hud-about"
      data-state={phase}
      data-reduced={reduced || undefined}
      data-kiosk={kiosk || undefined}
      aria-labelledby={titleId}
    >
      <div className="ld-loc-hud-about-scrim" onClick={requestClose} aria-hidden="true" />
      <div className="ld-loc-hud-about-sheet ld-paper">
        <header className="ld-loc-hud-about-head">
          <div className="ld-loc-hud-about-heading">
            <span className="ld-meta">{HUD_COPY.aboutKicker}</span>
            <h2 className="ld-h3 ld-loc-hud-about-title" id={titleId} ref={titleRef} tabIndex={-1}>
              {HUD_COPY.about}
            </h2>
          </div>
          <IconButton icon="close" label={HUD_COPY.aboutClose} variant="paper" tip={null} onClick={requestClose} />
        </header>
        <svg className="ld-loc-hud-about-ridge" viewBox="0 0 600 22" preserveAspectRatio="none" aria-hidden="true">
          <path d={RIDGE_D} />
        </svg>

        <div className="ld-loc-hud-about-scroll">
          <p className="ld-loc-hud-about-lead">{HUD_COPY.aboutLead}</p>

          <section className="ld-loc-hud-about-sec" aria-labelledby={`${titleId}-t`}>
            <h3 className="ld-meta ld-loc-hud-about-h" id={`${titleId}-t`}>
              <Icon name="clock" size={16} />
              {HUD_COPY.sectionTimes}
            </h3>
            <div className="ld-loc-hud-about-body">
              <p>{date ? HUD_COPY.times(date) : HUD_COPY.timesUndated}</p>
              <p>{HUD_COPY.offPeak}</p>
              <p className="ld-loc-hud-about-small">
                {HUD_COPY.distances}
                {counts.advised > 0 && ` ${HUD_COPY.advised}`}
              </p>
            </div>
          </section>

          <section className="ld-loc-hud-about-sec" aria-labelledby={`${titleId}-f`}>
            <h3 className="ld-meta ld-loc-hud-about-h" id={`${titleId}-f`}>
              <Icon name="ringRoad" size={16} />
              {HUD_COPY.sectionFuture}
            </h3>
            <div className="ld-loc-hud-about-body">
              <p>{HUD_COPY.future}</p>
              {rows.length > 0 && (
                <ul className="ld-loc-hud-about-corridors">
                  {rows.map((r) => (
                    <li key={r.id}>
                      <div className="ld-loc-hud-about-corridor-head">
                        <span className="ld-loc-hud-about-corridor-name">{r.name}</span>
                        <Status s={r.status} />
                      </div>
                      <p className="ld-loc-hud-about-small">{HUD_COPY.futureSource(r.source)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="ld-loc-hud-about-sec" aria-labelledby={`${titleId}-s`}>
            <h3 className="ld-meta ld-loc-hud-about-h" id={`${titleId}-s`}>
              <Icon name="pin" size={16} />
              {HUD_COPY.sectionSite}
            </h3>
            <div className="ld-loc-hud-about-body">
              <p>{HUD_COPY.site}</p>
            </div>
          </section>

          <section className="ld-loc-hud-about-sec" aria-labelledby={`${titleId}-p`}>
            <h3 className="ld-meta ld-loc-hud-about-h" id={`${titleId}-p`}>
              <Icon name="mapPin" size={16} />
              {HUD_COPY.sectionPins}
            </h3>
            <div className="ld-loc-hud-about-body">
              <p>{HUD_COPY.pins}</p>
              {counts.total > 0 && <p className="ld-loc-hud-about-small ld-num">{pinsBreakdown(counts)}</p>}
            </div>
          </section>

          <section className="ld-loc-hud-about-sec" aria-labelledby={`${titleId}-d`}>
            <h3 className="ld-meta ld-loc-hud-about-h" id={`${titleId}-d`}>
              <Icon name="globe" size={16} />
              {HUD_COPY.sectionData}
            </h3>
            <div className="ld-loc-hud-about-body">
              <p>{HUD_COPY.data}</p>
              <p>{HUD_COPY.derived}</p>
              {!kiosk && (
                <ul className="ld-loc-hud-about-links">
                  {HUD_COPY.links.map((l) => (
                    <li key={l.href}>
                      <a className="ld-loc-hud-about-link" href={l.href} target="_blank" rel="noopener noreferrer">
                        {l.label}
                        <Icon name="external" size={14} />
                        <span className="ld-sr"> {HUD_COPY.newTab}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <footer className="ld-loc-hud-about-foot">
          <span className="ld-powered ld-loc-hud-powered-paper">
            {HUD_COPY.poweredBy} <b>{HUD_COPY.poweredName}</b>
          </span>
          {prepared && <span className="ld-meta ld-num">{HUD_COPY.prepared(prepared)}</span>}
        </footer>
      </div>
    </dialog>
  );
}
